import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createAppTheme } from './theme';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ThemeMode = 'light' | 'dark';

interface ThemeModeContextValue {
  /** The active colour mode. */
  mode: ThemeMode;
  /** Toggle between light and dark. */
  toggleMode: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'bemotechs-theme-mode';
const MEDIA_QUERY = '(prefers-color-scheme: dark)';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(
  undefined,
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Determine the initial theme mode.
 *
 * Priority:
 * 1. Value persisted in localStorage
 * 2. Operating system preference via `prefers-color-scheme`
 * 3. Fallback to 'light'
 */
function getInitialMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch {
    // localStorage may be unavailable (e.g. SSR, private browsing).
  }

  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia(MEDIA_QUERY).matches ? 'dark' : 'light';
  }

  return 'light';
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface AppThemeProviderProps {
  children: React.ReactNode;
}

export const AppThemeProvider: React.FC<AppThemeProviderProps> = ({
  children,
}) => {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode);

  // Persist every mode change to localStorage.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Silently ignore write failures.
    }
  }, [mode]);

  // Listen for OS-level colour-scheme changes while no explicit preference
  // has been persisted yet.
  useEffect(() => {
    const mql = window.matchMedia(MEDIA_QUERY);

    const handler = (e: MediaQueryListEvent) => {
      // Only react to OS changes if the user hasn't explicitly toggled.
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };

    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const contextValue = useMemo<ThemeModeContextValue>(
    () => ({ mode, toggleMode }),
    [mode, toggleMode],
  );

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the current theme mode and toggle function.
 *
 * Must be used within an `<AppThemeProvider>`.
 */
export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (ctx === undefined) {
    throw new Error('useThemeMode must be used within an <AppThemeProvider>');
  }
  return ctx;
}
